/**
 * 直播间分享深链，与原生约定 path：`live/<stream_name>`。
 *
 * App 端（qipao-app）`RouteService` 会把
 * - `qipao://live/<stream_name>` 映射为内部路由 `/live/<stream_name>`（直播间页），
 * - 微信 `wx-open-launch-app` extinfo `{ path: 'live/<stream_name>' }` 走
 *   `handleWxExtinfo` 同样归一成 `/live/<stream_name>`。
 *
 * 直播间页深链冷启动时用 `stream_name` 调 `/api/v1/live/detail` 换播放地址，
 * 因此这里传流名（与 App 端分享链接 `#/live/<流名>` 同口径），不是房间数字 ID。
 */
import { ANDROID_QIPAO_PACKAGE } from './activityWebviewAppLink';

export { ANDROID_QIPAO_PACKAGE };

export const LIVE_PATH_PREFIX = 'live';

/**
 * @param {object} p
 * @param {string} p.streamName 直播流名（唯一标识，必传）
 * @returns {string} 深链 path 段：`live/<stream_name>`
 */
export function buildLivePath(p) {
  const name = String(p?.streamName || '').trim();
  if (!name) return '';
  return `${LIVE_PATH_PREFIX}/${encodeURIComponent(name)}`;
}

/**
 * 深链 query 串：主播 uid（可选）。App 端 `/live/detail` 不下发主播资料，
 * 直播间页靠它补拉 `/user/info/all`（信息卡昵称 / 头像 / 粉丝数）。
 * @param {object} p
 * @param {string} [p.uid]
 * @returns {string} `uid=xxx` 或空串
 */
function buildLiveQueryString(p) {
  const uid = String(p?.uid || '').trim();
  return uid ? `uid=${encodeURIComponent(uid)}` : '';
}

/**
 * 用于 iframe / iOS：`qipao://live/<stream_name>[?uid=xxx]`
 * @param {object} p
 * @param {string} p.streamName
 * @param {string} [p.uid]
 * @returns {string}
 */
export function buildQipaoLiveScheme(p) {
  const path = buildLivePath(p);
  if (!path) return '';
  const qs = buildLiveQueryString(p);
  return qs ? `qipao://${path}?${qs}` : `qipao://${path}`;
}

/**
 * Android：`intent://live/<stream_name>[?uid=xxx]#Intent;scheme=qipao;package=...;S.browser_fallback_url=...;end`
 * query 里的 uid 经 App 端 `intentUriToQipaoLink` 保留为 `qipao://live/..?uid=..`。
 * @param {object} p
 * @param {string} p.streamName
 * @param {string} [p.uid]
 * @param {string} pkg
 * @param {string} fallbackUrl
 * @returns {string}
 */
export function buildAndroidIntentLive(p, pkg, fallbackUrl) {
  const path = buildLivePath(p);
  if (!path) return '';
  const qs = buildLiveQueryString(p);
  return `intent://${path}${qs ? `?${qs}` : ''}#Intent;scheme=qipao;package=${pkg};S.browser_fallback_url=${encodeURIComponent(
    fallbackUrl
  )};end`;
}

/**
 * 微信 `wx-open-launch-app` 的 extinfo（OpenSDK messageExt）。
 * App 端 `handleWxExtinfo` 读取 `path` 与白名单字段（含 `uid`）并归一为内部路由。
 * @param {object} p
 * @param {string} p.streamName
 * @param {string} [p.uid]
 * @returns {string} JSON 字符串；流名为空时返回空串
 */
export function buildWxLiveExtinfo(p) {
  const path = buildLivePath(p);
  if (!path) return '';
  const uid = String(p?.uid || '').trim();
  return JSON.stringify(uid ? { path, uid } : { path });
}
